import type { ReactNode, Ref } from 'react';

import s from './PageHeader.module.css';

interface Props {
  /** The section of the product this screen belongs to — "Roster", "Setup". */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** One or two sentences: what this screen is for, or the state of its data. */
  subtitle?: ReactNode;
  /** The screen's controls. Right-aligned on a desktop, full-width on a phone. */
  actions?: ReactNode;
  /** Non-prose detail belonging to the title — an identity line, a warning. */
  meta?: ReactNode;
  /**
   * `page` is the <h1> of a screen. `section` is a detail view opened under a
   * screen that already has one, and takes the smaller of the two fluid roles.
   */
  level?: 'page' | 'section';
  /**
   * For screens that move focus to the heading after an action that replaced
   * the content below it. Passing this also makes the heading focusable —
   * `focus()` on an <h1> is a silent no-op without `tabIndex={-1}`, and that
   * pairing is easy to lose when the markup moves.
   */
  titleRef?: Ref<HTMLHeadingElement>;
  /** When something on the page is `aria-labelledby` the title. */
  titleId?: string;
}

/**
 * The top of a screen: eyebrow, title, subtitle, actions.
 *
 * Forty-two screens had built this by hand, and they had drifted — four
 * eyebrow colours, two subtitle measures, `align-items: flex-end` on some and
 * `flex-start` on others, so the same block sat at a different height
 * depending on which page you were on. None of it was visible until you put
 * two screens side by side, which is exactly the kind of inconsistency a
 * primitive exists to make impossible.
 *
 * Extras go in one of two slots and nowhere else: `meta` for something that
 * belongs to the title (a slug, a privilege warning), `actions` for anything
 * the user can operate. A screen that needs a third slot needs a rethink, not
 * a third prop.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  meta,
  level = 'page',
  titleRef,
  titleId,
}: Props): JSX.Element {
  const root = level === 'section' ? `${s.root ?? ''} ${s.section ?? ''}`.trim() : (s.root ?? '');
  return (
    <header className={root}>
      {/* The eyebrow takes its own full-width line rather than sitting inside
          the text column. With it in the column, the actions — which top-align
          with the column — ended up level with a 11px label instead of with
          the title they belong to, and floated visibly high. */}
      {eyebrow !== undefined ? <p className={s.eyebrow}>{eyebrow}</p> : null}
      <div className={s.main}>
        <div className={s.text}>
          <h1
            className={s.title}
            ref={titleRef}
            id={titleId}
            tabIndex={titleRef === undefined ? undefined : -1}
          >
            {title}
          </h1>
          {subtitle !== undefined ? <p className={s.subtitle}>{subtitle}</p> : null}
          {meta !== undefined ? <div className={s.meta}>{meta}</div> : null}
        </div>
        {actions !== undefined ? <div className={s.actions}>{actions}</div> : null}
      </div>
    </header>
  );
}
